/**
 * Zerbay product information bands (hz/52, PDPM-16 and PDPM-17).
 *  - gives the accordion rows of every [data-zb-bands] section their ids (data-zb-band-ids, in row order), on load and
 *    after an editor re-render;
 *  - a URL fragment naming a band id (also after "#zb-color&...") opens that band and scrolls it into view; hashchange too.
 *    The gallery zoom history adds "&gid=..&pid=.." with pushState: a hash with gid= (new or old URL) is never acted on;
 *  - per-colour text: shows the part [data-zb-colour-index] of the selected colour in every [data-zb-spec-value];
 *  - keeps the open / closed state the shopper chose when Horizon's accordion re-applies its defaults at 750 px.
 */
import { StandardEvents } from '@shopify/events';

const ROOT = '[data-zb-bands]';
const ROWS = '.accordion:not(.zb-bands__peak) > accordion-custom';
const COLOUR_INPUT = 'input[data-zb-colour-value]';
// the same places Horizon's variant picker ignores, plus any dialog
const FOREIGN_CONTEXT = 'product-card, quick-add-dialog, dialog';

const controller = new AbortController();
const { signal } = controller;

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** @param {string} hash */
const hashHasGid = (hash) => new URLSearchParams(hash.replace(/^#/, '')).has('gid');

/** @param {ParentNode} scope */
function assignIds(scope) {
  scope.querySelectorAll(ROOT).forEach((root) => {
    const ids = (root.getAttribute('data-zb-band-ids') || '').split(',').map((id) => id.trim());
    root.querySelectorAll(ROWS).forEach((row, index) => {
      if (ids[index]) row.id = ids[index];
    });
  });
}

/** @returns {HTMLElement | null} the band named by the address fragment (null for the zoom history entries) */
function bandFromHash() {
  if (hashHasGid(window.location.hash)) return null;

  const fragment = new URLSearchParams(window.location.hash.slice(1));
  for (const key of fragment.keys()) {
    const element = document.getElementById(key);
    if (element && element.matches(`${ROOT} accordion-custom`)) return element;
  }
  return null;
}

async function openFromHash() {
  const band = bandFromHash();
  if (!band) return;

  // accordion-custom resets its open state when it connects: wait for its definition before opening
  await customElements.whenDefined('accordion-custom');
  const details = band.querySelector('details');
  if (details && !details.open) details.open = true;
  requestAnimationFrame(() => {
    band.scrollIntoView({ block: 'start', behavior: reducedMotion() ? 'auto' : 'smooth' });
  });
}

/**
 * Index of the checked colour among the distinct colour values of a scope, in input order.
 * @param {ParentNode} scope
 * @returns {number} -1 when nothing is checked
 */
function checkedColourIndex(scope) {
  /** @type {string[]} */
  const values = [];
  let checked = -1;
  scope.querySelectorAll(COLOUR_INPUT).forEach((input) => {
    if (!(input instanceof HTMLInputElement)) return;
    const value = input.dataset.zbColourValue || '';
    let index = values.indexOf(value);
    if (index < 0) {
      index = values.length;
      values.push(value);
    }
    if (input.checked && checked < 0) checked = index;
  });
  return checked;
}

/** @param {number} index */
function showColour(index) {
  if (index < 0) return;
  document.querySelectorAll(`${ROOT} [data-zb-colour-index]`).forEach((part) => {
    if (part instanceof HTMLElement) part.hidden = Number(part.dataset.zbColourIndex) !== index;
  });
}

document.addEventListener(
  'change',
  (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches(COLOUR_INPUT)) return;
    if (input.closest(FOREIGN_CONTEXT)) return;
    showColour(checkedColourIndex(input.closest('.shopify-section') || document));
  },
  { signal }
);

document.addEventListener(
  StandardEvents.productSelect,
  async (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || target.closest(FOREIGN_CONTEXT)) return;
    try {
      // Horizon resolves { detail: { html } } with html already a parsed Document
      const { detail } = await event.promise;
      const doc = detail && detail.html;
      if (!doc || typeof doc.querySelectorAll !== 'function') return;
      const section = target.closest('.shopify-section');
      const scope = (section && section.id && doc.getElementById && doc.getElementById(section.id)) || doc;
      showColour(checkedColourIndex(scope));
    } catch (error) {
      // a cancelled or failed option change leaves the text as it is
    }
  },
  { signal }
);

/* Open / closed state chosen by the shopper (or by a link). accordion-custom re-applies its defaults when the viewport
   crosses 750 px; its listener runs before ours, so the state is read from what toggle events recorded earlier. */
const chosenState = new WeakMap();
let ignoreToggles = false;

document.addEventListener(
  'toggle',
  (event) => {
    const details = event.target;
    if (ignoreToggles || !(details instanceof HTMLDetailsElement)) return;
    if (details.closest(ROOT)) chosenState.set(details, details.open);
  },
  { capture: true, signal }
);

window.matchMedia('(min-width: 750px)').addEventListener(
  'change',
  () => {
    ignoreToggles = true;
    requestAnimationFrame(() => {
      document.querySelectorAll(`${ROOT} details`).forEach((details) => {
        if (chosenState.has(details)) details.open = chosenState.get(details);
      });
      setTimeout(() => {
        ignoreToggles = false;
      }, 100);
    });
  },
  { signal }
);

document.addEventListener(
  'shopify:section:load',
  (event) => {
    const section = event.target;
    if (section instanceof Element && (section.querySelector(ROOT) || section.matches(ROOT))) {
      assignIds(section);
      openFromHash();
    }
  },
  { signal }
);

window.addEventListener(
  'hashchange',
  (event) => {
    if (hashHasGid(new URL(event.oldURL).hash)) return;
    openFromHash();
  },
  { signal }
);

assignIds(document);
openFromHash();
